import { type SideNavItem, type SideNavSection } from '@dittolive/anvil'
import {
  ArrowsClockwiseIcon,
  ChartLineIcon,
  DatabaseIcon,
  DevicesIcon,
  FingerprintIcon,
  GearIcon,
  GlobeIcon,
  KeyIcon,
  PlugsConnectedIcon,
  ShieldStarIcon,
  TableIcon,
  TicketIcon,
} from '@phosphor-icons/react'
import paths, { type AppManagementArea } from 'paths'
import { useMemo } from 'react'

type Options = {
  // URL params for building links
  organizationSlug?: string
  appSlug: string
  /** Set when an admin views another user's app. */
  userId?: string
  // Permission gates
  showAuth?: boolean
  /** When the unified connection URL flag is on, the HTTPS page has no content of its own. */
  showHttpsConnect?: boolean
  isDomainReady?: boolean
  showCollections?: boolean
  showDevices?: boolean
  showAuthentication?: boolean
  showApiKeys?: boolean
  showLicenseTokens?: boolean
  allowMetrics?: boolean
  allowStreams?: boolean
  showSuperAdminSettings?: boolean
  showMongoConnector?: boolean
  // Prefetch callbacks
  onPrefetchAuth?: () => void
  onPrefetchSettings?: () => void
}

/**
 * Sidebar navigation for a single app, mirroring the tab structure that
 * `AppManagementScene` renders today. Sections with no visible items are
 * omitted.
 */
export function useAppNavSections(options: Options): SideNavSection[] {
  const {
    organizationSlug,
    appSlug,
    userId,
    showAuth,
    showHttpsConnect,
    isDomainReady,
    showCollections,
    showDevices,
    showAuthentication,
    showApiKeys,
    showLicenseTokens,
    allowMetrics,
    allowStreams,
    showSuperAdminSettings,
    showMongoConnector,
    onPrefetchAuth,
    onPrefetchSettings,
  } = options

  return useMemo(() => {
    const linkTo = (area: AppManagementArea): string =>
      paths.appManagement({
        organizationSlug,
        appSlug,
        tenantId: userId,
        area,
      })

    const connectItems: SideNavItem[] = showAuth
      ? [
          {
            text: 'SDK',
            to: linkTo('connect/sdk'),
            icon: <ArrowsClockwiseIcon />,
            onMouseEnter: onPrefetchAuth,
          },
          // With the unified connection URL flag on, the connect/* router
          // mounts no https route, so linking there would fall through the
          // catch-all and silently redirect to the SDK page.
          ...(showHttpsConnect
            ? [
                {
                  text: 'HTTPS',
                  to: linkTo('connect/https'),
                  icon: <GlobeIcon />,
                  onMouseEnter: onPrefetchAuth,
                },
              ]
            : []),
        ]
      : []

    const accessItems: SideNavItem[] = [
      ...(showAuthentication
        ? [
            {
              text: 'Authentication',
              to: linkTo('access/authentication'),
              icon: <FingerprintIcon />,
              onMouseEnter: onPrefetchAuth,
            },
          ]
        : []),
      ...(showApiKeys
        ? [
            {
              text: 'API keys',
              to: linkTo('access/api-keys'),
              icon: <KeyIcon />,
              onMouseEnter: onPrefetchAuth,
            },
          ]
        : []),
      ...(showLicenseTokens
        ? [
            {
              text: 'Offline license tokens',
              to: linkTo('access/license-tokens'),
              icon: <TicketIcon />,
              onMouseEnter: onPrefetchAuth,
            },
          ]
        : []),
    ]

    const dataItems: SideNavItem[] = isDomainReady
      ? [
          ...(showCollections
            ? [
                {
                  text: 'Collections',
                  to: linkTo('collections'),
                  icon: <TableIcon />,
                },
              ]
            : []),
          ...(showDevices
            ? [
                {
                  text: 'Devices',
                  to: linkTo('devices'),
                  icon: <DevicesIcon />,
                },
              ]
            : []),
          ...(allowStreams
            ? [
                {
                  text: 'Change Data Capture',
                  to: linkTo('cdc'),
                  icon: <DatabaseIcon />,
                },
              ]
            : []),
        ]
      : []

    const observeItems: SideNavItem[] = allowMetrics
      ? [
          {
            text: 'Metrics',
            to: linkTo('metrics'),
            icon: <ChartLineIcon />,
          },
        ]
      : []

    const settingsItems: SideNavItem[] = [
      {
        text: 'General',
        to: linkTo('settings/general'),
        icon: <GearIcon />,
        onMouseEnter: onPrefetchSettings,
      },
      ...(showMongoConnector
        ? [
            {
              text: 'MongoDB Connector',
              to: linkTo('settings/mongo'),
              icon: <PlugsConnectedIcon />,
            },
          ]
        : []),
      ...(showSuperAdminSettings
        ? [
            {
              text: 'Super admin',
              to: linkTo('settings/super-admin'),
              icon: <ShieldStarIcon />,
              onMouseEnter: onPrefetchSettings,
            },
          ]
        : []),
    ]

    return [
      ...(connectItems.length > 0
        ? [{ label: 'Connect', items: connectItems }]
        : []),
      ...(dataItems.length > 0 ? [{ label: 'Data', items: dataItems }] : []),
      ...(accessItems.length > 0
        ? [
            {
              label: 'Access',
              showInBreadcrumb: true,
              items: accessItems,
            },
          ]
        : []),
      ...(observeItems.length > 0
        ? [{ label: 'Observe', items: observeItems }]
        : []),
      { label: 'Settings', showInBreadcrumb: true, items: settingsItems },
    ]
  }, [
    organizationSlug,
    appSlug,
    userId,
    showAuth,
    showHttpsConnect,
    isDomainReady,
    showCollections,
    showDevices,
    showAuthentication,
    showApiKeys,
    showLicenseTokens,
    allowMetrics,
    allowStreams,
    showSuperAdminSettings,
    showMongoConnector,
    onPrefetchAuth,
    onPrefetchSettings,
  ])
}
